import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'negotiate-your-salary-before-you-walk-in',
  title: 'Win the Salary Negotiation Before You Walk In',
  description:
    'Learn how to negotiate salary before the meeting: set three numbers, build a three-point case, and rehearse your ask out loud with Sage, your AI career coach.',
  category: 'Career',
  icon: 'handshake',
  publishedAt: '2026-08-30',
  author: BLOG_AUTHOR,
  product: 'chot-buddies',
  buddy: 'Sage',
  keywords: ['how to negotiate salary', 'salary negotiation practice', 'ask for a raise', 'negotiation script'],
  body: `You have the offer in your inbox, or the review on your calendar, and you know you should say something about the number. So you open a note on your phone, type a few half-sentences, and close it again. Then the meeting starts and you hear yourself say, "Whatever you think is fair."

That sentence is easy to say and expensive to take back, and it is a preparation problem, not a talent problem. Here is how to negotiate salary well: decide your numbers before you walk in, then practice saying them out loud, which most people skip.

## How to negotiate salary: do the work before the meeting

You rehearse a toast. You run through a presentation. You do not improvise wedding vows. Yet a conversation that can shape your income for years usually gets zero rehearsal, because saying your number to another human feels awkward, even in an empty room.

Awkward is why it needs practice. The first time you say "I'm looking for" followed by a figure should not be in front of the person deciding it. The steps below take about an hour: 20 minutes on research, 20 on your case, 20 saying it aloud. The last step is the one most people skip.

## Know your range, then pick three numbers

Start with the market. Check reputable salary sources for your role, location and experience level: published salary surveys, job listings that show pay ranges, professional associations, and people in your field who are willing to tell you what is normal. Check at least three sources, and treat what you find as a range, not a promise.

Then decide three numbers before the meeting and write them down:

1. **Your target.** The figure you would be happy to land on. This is also where your opening ask should sit or just above it.
2. **Your floor.** The lowest total package you would accept and still feel good about the job.
3. **Your walk-away.** The point below which you would decline, and what you will do instead.

Knowing your walk-away is what keeps your voice steady. When you know what happens if the answer is no, "no" stops feeling like a cliff.

## Build your case and write your opening line

A number alone is a request. A number plus evidence is a conversation. Pick **three evidence points** that show your value, and make each one concrete:

- A result you delivered, with the measurable part if you have one ("I took over the reporting process and cut turnaround from two days to one").
- A responsibility you have grown into that is bigger than your current title or pay band.
- A skill or outcome the company needs next, and why you are the person to deliver it.

For a new offer, swap the second point for what you bring that the posting did not ask for. Keep each point to a sentence or two. You are not arguing a court case; you are making it easy for the other person to say yes to their own manager.

Now write your **opening sentence** word for word, because the first line is the hardest one to find on the spot. Something like: "I'm excited about this role, and based on my research and what I'd be taking on, I'm looking for a base salary of X." State the number, then stop talking. The silence will feel endless. Count to five in your head before you say anything.

## Handle the pushbacks, and look beyond base pay

You will likely hear one of three replies. Prepare a calm answer to each.

### "There's no budget right now."

Acknowledge it, then ask what is possible. "I understand budgets are tight. What flexibility is there on the package, or on timing?" This keeps the door open and moves the conversation toward the other items below.

### "Let's revisit this in six months."

Try to turn a vague later into a specific commitment. "I'm happy to plan around that. Can we agree on the goals that would justify an increase, and put the review date in writing?"

### "That's above the band for this role."

Ask what the band is based on and whether the scope of the role fits it. "Help me understand how the band was set. If my responsibilities go beyond it, is there a path to a different level?"

### Negotiate beyond base pay

If base salary cannot move, other things might. Depending on the employer, you could ask about:

- A different job title that matches the work you do
- A signing or performance bonus
- Equity or other long-term incentives
- Remote or flexible days
- A learning or conference budget
- Extra vacation time
- A guaranteed salary review date, in writing

Decide which of these matter to you before the meeting, so you are not trading away something you care about for something you do not.

## Rehearse out loud with Sage

Reading your script silently is not the same as saying it. Sage, the Career Coach in [Chot Buddies](/products/chot-buddies/), lets you run the whole conversation as many times as you like, with nobody watching.

Switch on **voice mode** and say your number out loud. Notice whether your voice rises at the end like a question. Say it again until it sounds like a statement. This is the closest thing to a dress rehearsal that fits in your pocket, and it is the same idea behind a [mock interview](/blog/mock-interview-in-your-pocket/).

Here are three prompts to paste in:

> Play a skeptical hiring manager. I'm negotiating the salary for a [your role] position. Let me open with my ask, then push back on me with "there's no budget," and wait for my reply before you continue.

> Here are my three evidence points: [paste them]. Tell me which one is weakest, and help me rewrite it so it's specific and easy to repeat out loud.

> I'm going to say my opening sentence. Listen for hedging words like "just," "maybe" or "sorry," and tell me how to make it sound confident without sounding pushy.

Want a rougher ride? Ask Sage to be impatient, or to answer "let's revisit in six months" three times in a row. For an even blunter second opinion on your pitch, [Scarlett does that too](/blog/honest-feedback-when-everyone-says-yes/).

One honest note: Sage is a practice partner and a thinking aid. It is not a financial or legal advisor, it cannot know your employer's budget, and it cannot promise you a raise or an offer. Use it to get your words and your nerves ready, and check anything about contracts or your rights with a qualified professional.

## Walk in calm

On the day, a few small habits help. Wear something comfortable that makes you feel put together; our guide to [dressing for the interview](/blog/dressing-for-interview-date-wedding/) covers that. Breathe before you answer. Ask a question instead of rushing to fill a pause. And remember that "I'd like to think about it and get back to you" is a good response to any offer.

You do not need to be a natural negotiator. You need your range, your three numbers, your evidence, one practiced sentence, and a few rounds of saying it aloud. Tonight, write your opening sentence, open Chot Buddies, and say it to Sage five times. Then ask her to push back with "there's no budget" and answer out loud.`,
};

export default post;
